import type { BudgetStatus, OrderType, TenderSummary } from '@/types/domain';

/** Labels for Order type and the Additional-budget tag. Single source for forms, tables, filters and exports. */
export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  original: 'Original',
  repeat: 'Repeat',
};
export const ORDER_TYPES = Object.keys(ORDER_TYPE_LABELS) as OrderType[];
export const orderTypeLabel = (v?: OrderType | null) => ORDER_TYPE_LABELS[v ?? 'original'] ?? v;

export const BUDGET_STATUS_LABELS: Record<BudgetStatus, string> = {
  none: 'None',
  needed: 'Needed',
  requested: 'Requested',
  approved: 'Approved',
  rejected: 'Rejected',
};
export const BUDGET_STATUSES = Object.keys(BUDGET_STATUS_LABELS) as BudgetStatus[];
export const budgetStatusLabel = (v?: BudgetStatus | null) => BUDGET_STATUS_LABELS[v ?? 'none'] ?? v;

/** Badge tone for the budget tag. */
export const BUDGET_STATUS_TONE: Record<BudgetStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  none: 'neutral',
  needed: 'warning',
  requested: 'info',
  approved: 'success',
  rejected: 'danger',
};

/** Needed or Requested — the "Needs additional budget" filter and the Dashboard KPI. */
export const isBudgetOpen = (s?: BudgetStatus | null) => s === 'needed' || s === 'requested';
/** Statuses only managers and admins may set. */
export const isBudgetDecision = (s?: BudgetStatus | null) => s === 'approved' || s === 'rejected';

/** Paid share of contract value at which the app suggests tagging "Needed". */
export const BUDGET_PAID_THRESHOLD = 0.9;

export interface BudgetSuggestion {
  reasons: string[];
}

/**
 * Suggest (never set automatically) the "Needed" tag when paid ≥ 90% of contract value, or when the
 * quantity pace projects running out before the contract end. Only for running contracts with no tag yet.
 * `runsOutOn` = the earliest projected run-out date of the tender (or its line items), if any.
 */
export function budgetSuggestion(
  t: Pick<TenderSummary, 'budgetStatus' | 'status' | 'contractValue' | 'paidAmount'>,
  runsOutOn: string | null,
  fmtDate: (iso: string) => string = (s) => s,
): BudgetSuggestion | null {
  if ((t.budgetStatus ?? 'none') !== 'none') return null;
  if (t.status !== 'active' && t.status !== 'expiring_soon') return null;
  const reasons: string[] = [];
  if (t.contractValue > 0 && t.paidAmount >= BUDGET_PAID_THRESHOLD * t.contractValue) {
    reasons.push(`${Math.floor((100 * t.paidAmount) / t.contractValue)}% of the contract value is already paid`);
  }
  if (runsOutOn) reasons.push(`at the current pace the quantity runs out around ${fmtDate(runsOutOn)}, before the contract ends`);
  return reasons.length ? { reasons } : null;
}
